/**
 * Tool annotations (MCP spec: title + readOnlyHint/destructiveHint).
 *
 * Centralized so classification lives in ONE reviewable table instead of 30
 * files, and so the registry test can enforce that every advertised tool is
 * annotated. Required for the Anthropic connectors directory ("All tools must
 * include a title and the applicable readOnlyHint or destructiveHint").
 *
 * Classification model:
 * - READ-ONLY: pure reads — name matches a read prefix or the exact-name set.
 * - DESTRUCTIVE: deletes/overwrites/terminates something that can't be undone
 *   from within the tool set.
 * - WRITE (everything else): creates or modifies state reversibly. The MCP
 *   spec DEFAULTS destructiveHint to true for non-read-only tools, so plain
 *   writes must say `destructiveHint: false` explicitly.
 */

import type { Tool } from "@modelcontextprotocol/sdk/types.js";

const READONLY_PREFIXES = [
  "list_",
  "get_",
  "search_",
  "inspect_",
  "describe_",
  "diagnose_",
  "plan_",
  "query_",
] as const;

/** Read-only tools whose names don't start with a read prefix. */
const READONLY_EXACT = new Set<string>([
  "validate_flow", // validates a definition; changes nothing
  "validate_model_driven_app", // validates an app; changes nothing
  "export_flow", // reads and returns the definition
  "pac_pages_list", // reads the PAC CLI profiles/sites; changes nothing
  "pac_copilot_list", // reads agents with PAC's existing profile; changes nothing
  "preview_update", // computes the diff an update would make; writes nothing
  "get_flow_version", // reads one saved version; changes nothing
  "get_trigger_inputs", // reads a past run's trigger payload
  "test_connection", // reads connection status; changes nothing
  "execute_fetchxml", // runs a FetchXML query; changes nothing
  "validate_copilot_agent", // lints an agent's components; changes nothing
]);

/** Irreversible (or practically irreversible) operations. */
const DESTRUCTIVE = new Set<string>([
  "delete_flow",
  "delete_copilot_agent", // deletes the agent and every component under it
  "delete_copilot_agent_component", // permanently deletes one authored agent component
  "delete_solution", // a MANAGED solution takes its components with it
  "disassociate_dataverse_rows", // removes a relationship link
  "batch_dataverse_operations", // may contain DELETE operations
  "delete_environment",
  "delete_dlp_policy",
  "delete_powerapp",
  "delete_powerapp_admin",
  "delete_custom_connector",
  "delete_dataverse_row",
  "delete_sharepoint_item",
  "delete_powerpages_component",
  "delete_powerpages_website",
  "delete_model_driven_app",
  "delete_canvas_source_file",
  "write_canvas_source_file", // can replace an existing source file when explicitly confirmed
  "sync_canvas_source", // overwrites same-named local source files
  "compile_canvas_source", // validates and synchronizes into the live Studio draft
  "export_solution", // can overwrite a caller-selected local ZIP path
  "download_copilot_agent_evaluation_snapshot", // can overwrite a caller-selected local ZIP path
  "set_copilot_agent_quarantine", // can block every normal channel for the agent
  "set_copilot_agent_connector_consent_bypass", // changes security consent behavior for connector calls
  "reassign_copilot_agent_owner", // removes the previous owner's access
  "import_solution", // can overwrite unmanaged customizations
  "remove_solution_component",
  // PAC Pages commands below can overwrite local content, deploy over site
  // configuration/code, or start/reset/revert migrations. Even when an
  // individual invocation happens to be additive, classify the tool by its
  // most destructive supported mode so hosts obtain confirmation first.
  "pac_pages_bootstrap_migrate",
  "pac_pages_clone",
  "pac_pages_download",
  "pac_pages_download_code_site",
  "pac_pages_migrate_datamodel",
  "pac_pages_upload",
  "pac_pages_upload_code_site",
  "reset_environment", // wipes the environment back to factory state
  "restore_environment", // overwrites the target environment with a backup
  "copy_environment", // overwrites the target environment with a copy
  "cancel_run", // terminates an in-flight run; the run can't be resumed
  "cancel_all_runs", // terminates every in-flight run of a flow
  "restore_flow_version", // overwrites the live definition with an older one
  "cancel_desktop_flow_run", // terminates an in-flight RPA run; the run can't be resumed
  "restart_hosted_machine", // reboots the machine; kills any in-flight run on it
  "delete_work_queue", // permanently deletes the queue and its items
  "delete_connection", // permanently deletes the connection; bound flows break
]);

/** Acronyms/brands the generated titles must render correctly. */
const TITLE_WORDS: Record<string, string> = {
  dlp: "DLP",
  ai: "AI",
  api: "API",
  apis: "APIs",
  openapi: "OpenAPI",
  powerapp: "Power App",
  powerapps: "Power Apps",
  powerpages: "Power Pages",
  sharepoint: "SharePoint",
};

/** Names whose generated title needs a human touch. */
const TITLE_OVERRIDES: Record<string, string> = {
  sign_in: "Sign In to Power Platform",
  list_powerapps_admin: "List Power Apps (Admin)",
  get_powerapp_admin: "Get Power App (Admin)",
  delete_powerapp_admin: "Delete Power App (Admin)",
  list_approvals_dataverse: "List Approvals (Dataverse)",
};

export function titleForTool(name: string): string {
  const override = TITLE_OVERRIDES[name];
  if (override) return override;
  return name
    .split("_")
    .map((word) => TITLE_WORDS[word] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export interface ToolAnnotations {
  title: string;
  readOnlyHint: boolean;
  destructiveHint?: boolean;
}

export function annotationsForTool(name: string): ToolAnnotations {
  const isReadOnly =
    READONLY_EXACT.has(name) || READONLY_PREFIXES.some((prefix) => name.startsWith(prefix));

  if (isReadOnly) {
    return { title: titleForTool(name), readOnlyHint: true };
  }
  return {
    title: titleForTool(name),
    readOnlyHint: false,
    destructiveHint: DESTRUCTIVE.has(name),
  };
}

/**
 * True when a tool irreversibly deletes/overwrites — the session-environment
 * banner uses this to warn before destructive work in a switched environment.
 */
export function isDestructiveToolName(name: string): boolean {
  return DESTRUCTIVE.has(name);
}

/** Stamp annotations onto every tool in place; returns the same array for chaining. */
export function annotateTools(tools: Tool[]): Tool[] {
  for (const tool of tools) {
    tool.annotations = { ...annotationsForTool(tool.name), ...tool.annotations };
  }
  return tools;
}
